import { Box, Typography, useMediaQuery } from "@mui/material";
import { motion } from "framer-motion";
import { useTheme } from "@mui/material/styles";
import { useTranslate } from "../Translate/TranslateContext";

const STEP_MAP = {
    user: ["user_registration", "terms_and_conditions", "email_verification"],
    farm: ["farm_registration", "data_setup", "finished"],
};

const CIRCLE = 34;
const CONNECTOR_H = 4;
const SHIMMER_W = 44;

export default function SetupBar({ Status, Type = "farm" }) {
    const { translate } = useTranslate();
    const theme = useTheme();
    const isCompact = useMediaQuery(theme.breakpoints.down("lg"));

    const steps = STEP_MAP[Type] ?? STEP_MAP.farm;
    const activeIndex = Math.max(steps.indexOf(Status), 0);

    return (
        <Box
            sx={{
                px: { xs: 2, md: 6 },
                py: 3,
                backgroundColor: "var(--color-green-soft)",
                borderBottom: "1px solid rgba(0,0,0,0.08)",
            }}
        >
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto 1fr",
                    rowGap: 2,
                    alignItems: "center",
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        gridColumn: "2",
                        fontWeight: 600,
                        color: "var(--color-green-strong)",
                        textAlign: "center",
                        whiteSpace: "nowrap",
                    }}
                >
                    {translate("setup_guide")}
                </Typography>

                <Box
                    sx={{
                        gridColumn: "1 / -1",
                        minWidth: 0,
                        overflowX: isCompact ? "auto" : "visible",
                        pb: isCompact ? 1 : 0,
                    }}
                >
                    <Box
                        sx={{
                            position: "relative",
                            display: "grid",
                            gridTemplateColumns: `repeat(${steps.length}, minmax(80px, 1fr))`,
                            alignItems: "start",
                            isolation: "isolate",
                        }}
                    >
                        {steps.map((_, i) => {
                            if (i === steps.length - 1) return null;

                            return (
                                <Box
                                    key={`connector-${i}`}
                                    sx={{
                                        position: "absolute",
                                        top: CIRCLE / 2,
                                        left: `${(100 / steps.length) * (i + 0.5)}%`,
                                        width: `${100 / steps.length}%`,
                                        height: CONNECTOR_H,
                                        backgroundColor: i < activeIndex
                                            ? "var(--color-green-main)"
                                            : "rgba(0,0,0,0.25)",
                                        overflow: "hidden",
                                        zIndex: 1,
                                    }}
                                >
                                    {i === activeIndex && (
                                        <Box
                                            component={motion.div}
                                            initial={{ left: -SHIMMER_W }}
                                            animate={{ left: "100%" }}
                                            transition={{
                                                repeat: Infinity,
                                                duration: 1.2,
                                                ease: "linear",
                                            }}
                                            sx={{
                                                position: "absolute",
                                                top: 0,
                                                bottom: 0,
                                                width: SHIMMER_W,
                                                background:
                                                    "linear-gradient(90deg, transparent, var(--color-green-main), transparent)",
                                            }}
                                        />
                                    )}
                                </Box>
                            );
                        })}

                        {steps.map((step, i) => {
                            const isActive = i === activeIndex;
                            const isCompleted = i < activeIndex;

                            return (
                                <Box
                                    key={step}
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: 1,
                                        position: "relative",
                                        zIndex: 10,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: CIRCLE,
                                            height: CIRCLE,
                                            borderRadius: "50%",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontWeight: 600,
                                            fontSize: "0.85rem",
                                            backgroundColor: isCompleted
                                                ? "var(--color-green-main)"
                                                : isActive
                                                ? "var(--color-green-strong)"
                                                : "var(--color-bg-primary)",
                                            color: isCompleted || isActive
                                                ? "var(--color-text-inverse)"
                                                : "var(--color-text-primary)",
                                            border: isCompleted || isActive
                                                ? "none"
                                                : "2px solid rgba(0,0,0,0.35)",
                                        }}
                                    >
                                        {i + 1}
                                    </Box>

                                    {!isCompact && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                textAlign: "center",
                                                fontWeight: isActive ? 600 : 400,
                                                color: isActive
                                                    ? "var(--color-green-strong)"
                                                    : "text.secondary",
                                                maxWidth: 120,
                                                lineHeight: 1.25,
                                                hyphens: "auto",
                                            }}
                                        >
                                            {translate(step)}
                                        </Typography>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}